import { useCallback, useContext, useEffect } from 'react';
import { areMapLocationsEqual, roundMapLocation } from 'app/agriculture/utils';
import { createMarker } from 'app/agriculture/hooks/useMapRender/mapRenderUtils';
import { MapLocation } from 'app/agriculture/queries/types/MapLocation';
import { useCesium } from 'app/agriculture/hooks/useCesium/useCesium';
import { mousePositionToLocation } from 'app/agriculture/hooks/useCesium/cesiumUtils';
import { ServicesContext } from 'app/items/shared/context_interface';
import { ScreenSpaceEventHandler, ScreenSpaceEventType, Viewer } from 'cesium';

interface LocationFieldProps {
    onChange: (value: MapLocation) => void;
    value?: MapLocation | null;
    isPin?: boolean;
}

function leftClick(
    event: ScreenSpaceEventHandler.PositionedEvent,
    setLocationCallback: (location: MapLocation) => void,
    viewer: Viewer,
) {
    const location = mousePositionToLocation(event.position.x, event.position.y, viewer);
    if (location) {
        setLocationCallback(location);
    }
}

// Displays the map marker at the specified location. When unmounted, no longer shows the map marker
export function RenderedMapMarker({ isPin = false, onChange, value }: LocationFieldProps) {
    const { markerCollection } = useCesium();
    const { mapInputHandlerService, mapService } = useContext(ServicesContext);

    const handleLocationSet = useCallback((location: MapLocation) => {
        const roundedLocation = roundMapLocation(location);
        if (!areMapLocationsEqual(value, roundedLocation)) {
            onChange(roundedLocation);
        }
    }, []);

    useEffect(() => {
        if (!mapService.viewer) return () => {};

        // TODO: Clean up the below when all Nu world refactoring complete
        void mapInputHandlerService.setActiveHandlerByName('noopHandler');

        const handler = new ScreenSpaceEventHandler(mapService.viewer.canvas);
        handler.setInputAction((event: ScreenSpaceEventHandler.PositionedEvent) => {
            leftClick(event, handleLocationSet, mapService.viewer);
        }, ScreenSpaceEventType.LEFT_CLICK);

        return () => {
            if (markerCollection) markerCollection.removeAll();
            // TODO: Clean up this bit up too
            void mapInputHandlerService.setDefaultHandler();
            handler.destroy();
        };
    }, [mapService.viewer]);

    if (!value || !markerCollection) return null;

    markerCollection.removeAll();
    const marker = createMarker({
        location: roundMapLocation(value),
        isPin: !!isPin,
    });

    markerCollection.add(marker);

    return null;
}
